<template>
  <div
    class="custom-textarea"
    :class="`${isDisabled && 'custom-textarea--disabled'}
              ${isError && 'custom-textarea--error'}
              ${isFocus && 'custom-textarea--active'}`"
  >
    <label class="custom-textarea__textarea-label">
      <textarea
        ref="input"
        v-model="val"
        :style="computedStyles"
        :disabled="isDisabled"
        :placeholder="placeholder"
        :maxlength="maxSymbols"
        :tabindex="tabindex"
        :class="`custom-textarea__textarea
                ${maxSymbols && count > 0 && 'custom-textarea__textarea--bottom-counter-padding'}`"
        @input="update()"
        @focus="
          ($event) => {
            isFocus = true;
            $emit('focus', $event);
            resize();
          }
        "
        @blur="
          ($event) => {
            isFocus = false;
            $emit('blur', $event);
          }
        "
      >
      </textarea>
    </label>
    <span v-if="maxSymbols && count > 0" class="custom-textarea__counter"> {{ count }}/{{ maxSymbols }} </span>
    <span v-if="isDisabled" class="custom-textarea__lock" v-html="svg.lock"> </span>
  </div>
</template>

<script>
import lock from '@assets/icons/other/ico-lock.svg';

export default {
  emits: ['update:modelValue', 'focus', 'blur'],

  props: {
    placeholder: String,
    modelValue: {
      type: [String, Number],
      default: '',
    },
    autosize: {
      type: Boolean,
      default: true,
    },
    minHeight: {
      type: Number,
      default: null,
    },
    maxHeight: {
      type: Number,
      default: null,
    },
    important: {
      type: [Boolean, Array],
      default: false,
    },
    maxSymbols: {
      type: [Boolean, Number, String],
      default: '1000',
    },
    isDisabled: Boolean,
    isError: Boolean,
    autoFocus: { type: Boolean, default: false },
    tabindex: { type: Number, default: 0 },
  },
  data() {
    return {
      count: '0',
      isFocus: false,
      val: null,
      maxHeightScroll: false,
      height: 'auto',
      svg: {
        lock,
      },
    };
  },
  computed: {
    computedStyles() {
      if (!this.autosize) return {};
      return {
        resize: !this.isResizeImportant ? 'none' : 'none !important',
        height: this.height,
        overflow: this.maxHeightScroll ? 'auto' : !this.isOverflowImportant ? 'hidden' : 'hidden !important',
      };
    },
    isResizeImportant() {
      const imp = this.important;
      return imp === true || (Array.isArray(imp) && imp.includes('resize'));
    },
    isOverflowImportant() {
      const imp = this.important;
      return imp === true || (Array.isArray(imp) && imp.includes('overflow'));
    },
    isHeightImportant() {
      const imp = this.important;
      return imp === true || (Array.isArray(imp) && imp.includes('height'));
    },
  },
  watch: {
    modelValue(val) {
      this.val = val;
    },
    val(val) {
      this.$nextTick(this.resize);
      this.$emit('update:modelValue', val);
    },
    minHeight() {
      this.$nextTick(this.resize);
    },
    maxHeight() {
      this.$nextTick(this.resize);
    },
    autosize(val) {
      if (val) this.resize();
    },
  },
  methods: {
    focus() {
      this.$refs.input.focus();
    },

    update() {
      this.$emit('update:modelValue', this.$refs.input.value);
      this.count = this.$refs.input.value.length;
    },

    resize() {
      const important = this.isHeightImportant ? 'important' : '';
      this.height = `auto${important ? ' !important' : ''}`;
      this.$nextTick(() => {
        // A value change queues this, and the textarea can be gone by the time it runs — anything
        // that unmounts it in the same tick (a v-if flipping on the new value) leaves the ref null.
        // It can also be there but not laid out: a deactivated <keep-alive> tab is detached from
        // the document and measures 0. Leave it at auto then; activated() measures again.
        if (!this.$refs.input?.scrollHeight) return;

        let contentHeight = this.$refs.input.scrollHeight + 1;
        if (this.minHeight) {
          contentHeight = contentHeight < this.minHeight ? this.minHeight : contentHeight;
        }
        if (this.maxHeight) {
          if (contentHeight > this.maxHeight) {
            contentHeight = this.maxHeight;
            this.maxHeightScroll = true;
          } else {
            this.maxHeightScroll = false;
          }
        }
        const heightVal = contentHeight + 'px';
        this.height = `${heightVal}${important ? ' !important' : ''}`;
      });
      return this;
    },
  },
  created() {
    this.val = this.modelValue;
  },
  async mounted() {
    this.resize();
    if (this.autoFocus) {
      await this.$nextTick();
      this.focus();
    }
  },
  activated() {
    this.resize();
  },
};
</script>

<style scoped lang="scss">
@import '@styles/variables';
@import '@styles/popover.scss';

.custom-textarea {
  padding: 0;
  display: flex;
  align-items: center;
  // Match the compact 3.1rem fs-input shape — same radius family so a text
  // area placed next to a single-line input reads as one consistent control.
  border-radius: 1.6rem;
  position: relative;
  z-index: 1;
  color: rgba($c-dark, 1);
  background-color: rgba($c-bg-gray-darkest, 0.5);
  transition:
    height 0.15s ease,
    background-color 0.1s ease;

  &:hover {
    background-color: rgba($c-bg-gray-darkest, 0.75);
  }

  &--active {
    background: rgba($c-bg-gray-darkest, 1);

    &:hover {
      background: rgba($c-bg-gray-darkest, 1);
    }
  }

  &--disabled {
    color: rgba($c-dark, 0.25) !important;
    background: rgba($c-bg-gray-darkest, 0.25) !important;

    :deep(textarea) {
      cursor: not-allowed;
    }
  }

  &--error {
    background: #ffbcbc !important;
  }

  &__textarea-label {
    width: 100%;
    height: 100%;
    max-height: 100%;
    padding: 1rem 1rem;
    box-sizing: border-box;
    display: flex;
    cursor: text;
    transition: padding 0.15s ease;

    &--bottom-counter-padding {
      padding-bottom: 2.3rem;
    }
  }

  &__textarea {
    width: 100%;
    height: 100%;
    max-height: 100%;
    padding: 0.8rem 1rem 0.8rem;
    box-sizing: border-box;
    overflow: hidden;
    background: none;
    margin: 0;
    border: none;
    color: inherit;
    font-size: 1.4rem;
    line-height: 1.9rem;
    outline: none;
    resize: none;

    &::placeholder {
      color: rgba($c-dark, 0.5);
    }

    &--bottom-counter-padding {
      height: calc(100% - 2.3rem);
    }

    &::-webkit-scrollbar {
      width: 0.8rem;
    }

    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0);
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 10rem;
      background: rgba(0, 0, 0, 0.3);
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 0, 0, 0.5);
    }
  }

  &__counter {
    position: absolute;
    inset: auto 2.4rem 1.4rem auto;
    font-size: 1rem;
    line-height: 1rem;
    opacity: 0.5;
    pointer-events: none;
  }

  &__lock {
    position: absolute;
    z-index: 2;
    top: 1.1rem;
    right: 2.4rem;
  }
}
</style>
